/**
 * FnoExpiryMenu — the FNO expiry switch on the chart legend (2026-11-03).
 *
 * One small chip ("FNO D0 · 04 Nov ▾"). Clicking it lists the rolling tiers (D0 / W0 / M0:
 * they follow the front contract and roll at its settlement) and every live expiry by date
 * (pinned: that contract's own history, no roll). Every FNO line, pane and the payoff OI
 * profile follow the choice.
 */
import React, { useState } from 'react';
import { Box, ListSubheader, Menu, MenuItem, Tooltip } from '@mui/material';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import PushPinIcon from '@mui/icons-material/PushPin';
import { CH_TYPE } from '../../styles/appUi';

export interface FnoSelectOption {
  value: string;           // 'D' | 'W' | 'M' | 'YYYY-MM-DD'
  label: string;           // 'D0' | '04 Nov'
  sub?: string;            // 'front expiry · 04 Nov' | 'weekly · 6d'
  disabled?: boolean;
  title?: string;
  group: 'rolling' | 'pinned';
}
export interface FnoSelectProps {
  value: string;
  /** What the chip shows, e.g. "D0 · 04 Nov" or "📌 27 Nov". */
  chip: string;
  pinned: boolean;
  options: FnoSelectOption[];
  onSelect: (value: string) => void;
}

const FnoExpiryMenu: React.FC<FnoSelectProps & { compact?: boolean }> = ({ value, chip, pinned, options, onSelect, compact }) => {
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const rolling = options.filter(o => o.group === 'rolling');
  const dates = options.filter(o => o.group === 'pinned');
  const item = (o: FnoSelectOption) => (
    <MenuItem
      key={o.value}
      dense
      selected={o.value === value}
      disabled={o.disabled}
      onClick={() => { setAnchor(null); if (o.value !== value) onSelect(o.value); }}
      sx={{ fontSize: CH_TYPE.small, minHeight: 28, gap: 1 }}
      title={o.title}
    >
      <Box component="span" sx={{ fontWeight: 700, minWidth: 46 }}>{o.label}</Box>
      {o.sub && <Box component="span" sx={{ color: 'text.secondary', fontSize: CH_TYPE.micro }}>{o.sub}</Box>}
    </MenuItem>
  );
  return (
    <>
      <Tooltip title="Which option expiry the FNO lines and panes show. D0/W0/M0 follow the front contract and roll at its settlement; a date pins that contract." placement="bottom-start">
        <Box
          component="button"
          type="button"
          data-testid="fno-expiry-chip"
          onClick={(e: React.MouseEvent<HTMLElement>) => { e.stopPropagation(); setAnchor(e.currentTarget); }}
          onMouseDown={(e: React.MouseEvent) => e.stopPropagation()}
          sx={{
            pointerEvents: 'auto', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 0.3,
            border: 1, borderColor: pinned ? 'warning.main' : 'divider', borderRadius: 1,
            bgcolor: 'transparent', color: 'text.primary', px: 0.5, py: 0, mr: 0.8,
            fontFamily: 'inherit', fontSize: CH_TYPE.small, fontWeight: 700, lineHeight: 1.4,
            '&:hover': { borderColor: 'primary.main' },
          }}
        >
          {pinned && <PushPinIcon sx={{ fontSize: 11, color: 'warning.main' }} />}
          <span>{compact ? chip.replace(/^FNO\s*/, '') : chip}</span>
          <ArrowDropDownIcon sx={{ fontSize: 14, mr: -0.3 }} />
        </Box>
      </Tooltip>
      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        onClick={e => e.stopPropagation()}
        slotProps={{ paper: { sx: { maxHeight: 420 } } }}
      >
        <ListSubheader sx={{ fontSize: CH_TYPE.micro, lineHeight: '24px' }}>Rolling — follows the front contract</ListSubheader>
        {rolling.map(item)}
        {dates.length > 0 && <ListSubheader sx={{ fontSize: CH_TYPE.micro, lineHeight: '24px' }}>Pin one expiry</ListSubheader>}
        {dates.map(item)}
      </Menu>
    </>
  );
};

export default FnoExpiryMenu;
